<template>
    <div class="panel">
        <h3>Pax search</h3>

        <Checkbox v-model="data.search.enabled" density="compact"> Use pax search </Checkbox>

        <TransitionExpand>
            <div class="panel-count" v-if="data.search.enabled">
                <Toast radius="m" title="NOTE" color="yellow" fullWidth columnMobile>
                    <span>
                        Specify max and min of each type of passenger to get a random number
                        of passengers.<br />
                        Minimum required for a product is one Adult.<br />
                        Eg.: min 2 max 5 => [2, 3, 4, 5]<br />
                        Eg.: min 2 max 2 => 2<br />
                        Eg.: min 0 max 0 => 0 (to avoid children or infants)
                    </span>
                </Toast>

                <div class="panel-count-row">
                    Adults
                    <div class="panel-count-row-input">
                        Min
                        <Count
                            buttonSize="s"
                            :min="1"
                            :max="5"
                            v-model="data.search.adults.min"
                            @click="handleMin('adults')" />
                    </div>
                    <div class="panel-count-row-input">
                        Max
                        <Count
                            buttonSize="s"
                            :min="1"
                            :max="5"
                            v-model="data.search.adults.max"
                            @click="handleMax('adults')" />
                    </div>
                </div>

                <div class="panel-count-row">
                    Children
                    <div class="panel-count-row-input">
                        Min
                        <Count
                            buttonSize="s"
                            :min="0"
                            :max="5"
                            v-model="data.search.children.min"
                            @click="handleMin('children')" />
                    </div>
                    <div class="panel-count-row-input">
                        Max
                        <Count
                            buttonSize="s"
                            :min="0"
                            :max="5"
                            v-model="data.search.children.max"
                            @click="handleMax('children')" />
                    </div>
                </div>

                <div class="panel-count-row">
                    Infants
                    <div class="panel-count-row-input">
                        Min
                        <Count
                            buttonSize="s"
                            :min="0"
                            :max="5"
                            v-model="data.search.infants.min"
                            @click="handleMin('infants')" />
                    </div>
                    <div class="panel-count-row-input">
                        Max
                        <Count
                            buttonSize="s"
                            :min="0"
                            :max="5"
                            v-model="data.search.infants.max"
                            @click="handleMax('infants')" />
                    </div>
                </div>
            </div>
        </TransitionExpand>
    </div>

    <div class="panel">
        <h3>Affiliate</h3>

        <Checkbox v-model="data.affiliate.enabled" density="compact">
            Has affiliate data
        </Checkbox>

        <TransitionExpand>
            <div class="panel-count" v-if="data.affiliate.enabled">
                <div class="panel-text">
                    <Textbox
                        v-model="data.affiliate.affiliateId"
                        label="Affiliate Id"
                        placeholder="Example: 70108" />
                </div>

                <div class="panel-select">
                    <Select
                        v-model="data.affiliate.salesChannel"
                        :items="salesChannels"
                        label="Sales Channel"
                        placeholder="Choose type" />
                </div>

                <div class="panel-select">
                    <Select
                        v-model="data.affiliate.businessArea"
                        :items="businessAreas"
                        label="Business Area"
                        placeholder="Choose type" />
                </div>

                <div class="panel-text">
                    <Textbox
                        v-model="data.affiliate.groupName"
                        label="Group Name"
                        placeholder="Example: Idealo" />
                </div>

                <div class="panel-text">
                    <Textbox
                        v-model="bookingSalesChannelsText"
                        label="Booking sales channels"
                        placeholder="Example: default, planner" />
                    <em>
                        {{ data.bookingSalesChannels }}
                    </em>
                </div>
            </div>
        </TransitionExpand>
    </div>

    <div class="panel">
        <h3>Page data</h3>

        <div>
            <Checkbox v-model="data.hasAgency" density="compact"> Has agency </Checkbox>
            <Checkbox v-model="data.hasAddonServicePackage" density="compact">
                Has service levels
            </Checkbox>
            <Checkbox v-model="data.hasAddonLuggage" density="compact">
                Has luggage addon
            </Checkbox>
            <Checkbox v-model="data.hasAddonCancellation" density="compact">
                Has cancellation addon
            </Checkbox>
            <Checkbox v-model="data.hasAddonTravelInsurance" density="compact">
                Has travel insurance addon
            </Checkbox>
            <Checkbox v-model="data.hasPaymentDiscount" density="compact">
                Has payment discount
            </Checkbox>
            <Checkbox v-model="data.hasCampaignDiscount" density="compact">
                Has campaign discount
            </Checkbox>
            <Checkbox v-model="data.hasAmountAlreadyPaid" density="compact">
                Has amount already paid
            </Checkbox>

            <div class="panel-select">
                <Select
                    v-model="data.cancellationType"
                    :items="cancellationOptions"
                    label="Cancellation Type"
                    placeholder="Choose type" />
                <Select
                    v-model="data.combinationIdType"
                    :items="combinationTypes"
                    label="Combination products"
                    placeholder="Choose type" />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType, computed, ref, watch } from 'vue';
    import { FakeDataCheckout } from '../DevPanel.interfaces';
    import {
        getCombinationTypeOptions,
        getBusinessAreaOptions,
        getSalesChannelsOptions,
        getCancellationTypeOptions,
    } from '../DevPanel.helpers';
    import TransitionExpand from '@ui/components/TransitionExpand/TransitionExpand.vue';
    import Select from '@ui/components/FormV2/Fields/Select/Select.vue';
    import Checkbox from '@ui/components/FormV2/Fields/Checkbox/Checkbox.vue';
    import Count from '@ui/components/FormV2/Fields/Count/Count.vue';
    import Textbox from '@ui/components/FormV2/Fields/Textbox/Textbox.vue';
    import Toast from '@ui/components/Toast/Toast.vue';

    export default defineComponent({
        name: 'CheckoutPanel',
        components: {
            Toast,
            TransitionExpand,
            Select,
            Textbox,
            Checkbox,
            Count,
        },
        props: {
            data: {
                type: Object as PropType<FakeDataCheckout>,
                required: true,
            },
        },
        setup({ data }) {
            const bookingSalesChannelsText = ref<string>(
                data.bookingSalesChannels?.join(',')
            );

            watch(
                () => bookingSalesChannelsText.value,
                (value) => {
                    data.bookingSalesChannels = value.split(',');
                }
            );

            const combinationTypes = computed(() => getCombinationTypeOptions());

            const cancellationOptions = computed(() => getCancellationTypeOptions());

            const salesChannels = computed(() => getSalesChannelsOptions());
            const businessAreas = computed(() => getBusinessAreaOptions());

            function handleMin(type: 'adults' | 'children' | 'infants') {
                const min = data.search[type].min;
                const max = data.search[type].max;

                if (min > max) {
                    data.search[type].max = min;
                }
            }

            function handleMax(type: 'adults' | 'children' | 'infants') {
                const min = data.search[type].min;
                const max = data.search[type].max;

                if (max < min) {
                    data.search[type].min = max;
                }
            }

            return {
                bookingSalesChannelsText,
                combinationTypes,
                cancellationOptions,
                salesChannels,
                businessAreas,
                handleMin,
                handleMax,
            };
        },
    });
</script>

<style lang="scss">
    @forward './Panel.scss';
</style>
